import { useState } from 'react'
import { X, Lock, Users } from 'lucide-react'
import { Button, Input } from '@/components/ui'
import { useCreateBoard } from '../hooks/useBoards'
import { useCompanies } from '@/features/companies/hooks/useCompanies'
import { cn } from '@/lib/cn'
import { BOARD_BACKGROUND_PRESETS, BOARD_SOLID_BACKGROUNDS } from './boardBackgrounds'

const CREATE_BOARD_BACKGROUNDS = [
  ...BOARD_BACKGROUND_PRESETS.map(({ name, value }) => ({ name, value })),
  ...BOARD_SOLID_BACKGROUNDS
    .filter((color) => color !== '#ffffff')
    .map((color) => ({ name: color, value: color })),
]

interface CreateBoardModalProps {
  open: boolean
  onClose: () => void
}

export function CreateBoardModal({ open, onClose }: CreateBoardModalProps) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [background, setBackground] = useState('')
  const [visibility, setVisibility] = useState<'workspace' | 'private'>('private')
  const [companyId, setCompanyId] = useState<string>('')
  
  const createBoard = useCreateBoard()
  const { data: companies } = useCompanies()

  if (!open) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    createBoard.mutate({
      title: title.trim(),
      description: description.trim() || undefined,
      background,
      visibility,
      entityType: companyId ? 'company' : undefined,
      entityId: companyId || undefined,
    })
    setTitle('')
    setDescription('')
    setBackground('')
    setVisibility('private')
    setCompanyId('')
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close"
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-white/50 bg-white p-6 shadow-2xl dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Create board
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Preview */}
        <div
          className="mb-5 flex h-24 items-end rounded-xl p-4 transition-colors duration-200"
          style={{ background: background || '#ffffff' }}
        >
          <span className={cn('text-sm font-semibold drop-shadow-sm', background ? 'text-white/90' : 'text-slate-700')}>
            {title || 'Board title'}
          </span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Board title <span className="text-red-500">*</span>
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Sprint 1, Sales Pipeline, Hiring..."
              autoFocus
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={2}
              className="box-border w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
            />
          </div>

          {/* Visibility */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Visibility
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setVisibility('workspace')}
                className={cn(
                  'flex items-center gap-2 rounded-lg border p-2.5 text-xs font-medium transition-all text-left',
                  visibility === 'workspace'
                    ? 'border-primary bg-primary/10 text-primary dark:bg-primary/20'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-400'
                )}
              >
                <Users className="h-4 w-4 shrink-0" />
                <div>
                  <div className="font-semibold">Workspace</div>
                  <div className="text-[10px] opacity-80">All team members</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setVisibility('private')}
                className={cn(
                  'flex items-center gap-2 rounded-lg border p-2.5 text-xs font-medium transition-all text-left',
                  visibility === 'private'
                    ? 'border-primary bg-primary/10 text-primary dark:bg-primary/20'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-400'
                )}
              >
                <Lock className="h-4 w-4 shrink-0" />
                <div>
                  <div className="font-semibold">Private</div>
                  <div className="text-[10px] opacity-80">Board members only</div>
                </div>
              </button>
            </div>
          </div>

          {/* Company Link */}
          {companies && companies.length > 0 && (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Company (Optional)
              </label>
              <div className="relative">
                <select
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                  className="box-border w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="">All / Workspace-wide</option>
                  {companies.map((comp) => (
                    <option key={comp._id} value={comp._id}>
                      {comp.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Background</label>
            <div className="flex flex-wrap gap-2">
              {CREATE_BOARD_BACKGROUNDS.map((option) => (
                <button
                  key={option.name}
                  type="button"
                  onClick={() => setBackground(option.value)}
                  className={cn(
                    'h-8 w-8 rounded-lg border-2 transition-transform duration-150 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary/40',
                    background === option.value ? 'border-primary ring-2 ring-primary/30' : !option.value ? 'border-gray-300 dark:border-gray-500' : 'border-transparent',
                  )}
                  style={{ background: option.value || '#ffffff' }}
                  title={option.name}
                >
                  <span className="sr-only">{option.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!title.trim()} loading={createBoard.isPending}
            >
              {createBoard.isPending ? 'Creating…' : 'Create board'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

CreateBoardModal.displayName = 'CreateBoardModal'
